'use es6';

import { fromJS } from 'immutable';
import { List } from 'immutable';
import { Config } from '../..';
import { FUNNEL, PIPELINE, SEARCH } from '../../../constants/configTypes';
import * as DataTypes from '../../../constants/dataTypes';
import { CONTACTS, DEALS } from '../../../constants/dataTypes';
import { RANGE_TYPES } from '../../../constants/dateRangeTypes';
import { COUNT } from '../../../constants/metricTypes';
import { DRILLDOWN_TYPES } from '../../../constants/trackingMetrics';
import { getUseFiscalYear } from '../../../lib/fiscalYearUtils';
import { getLifecycleStagePropertyOptions } from '../../../properties/partial/contacts-lifecyclestages';
import getDealStages from '../../../retrieve/inboundDb/aggregate/generators/deal-stages';
import generateSearchFilter from '../../../retrieve/inboundDb/aggregate/generators/search-filter';
import { Metric } from '../../metrics';
import { defaultProperties } from '../defaultProperties';
import { buildDrilldownReport } from '../report';
const contactStageToProperty = stage => `hs_v2_date_entered_${stage}`;
const dealStageToProperty = (stage, metaDataForStages) => {
  const dateEnteredStagePropertyName = metaDataForStages.find(stageData => stageData.get('value') === stage).get('dateEnteredStagePropertyName');
  return dateEnteredStagePropertyName.replace('hs_date_entered_', 'hs_v2_date_entered_');
};
const stageToPropertyForDataType = {
  [DataTypes.CONTACTS]: contactStageToProperty,
  [DataTypes.DEALS]: dealStageToProperty
};
const stageToProperty = (dataType, stage, metaDataForStages) => stage === 'create' ? 'createdate' : stageToPropertyForDataType[dataType](stage, metaDataForStages);
const createFunnelStageFilter = (stage, dataType, dateRange, useFiscalYear = false, metaDataForStages) => {
  const property = stageToProperty(dataType, stage, metaDataForStages);
  return generateSearchFilter(property, dateRange, useFiscalYear);
};
const getStagesByDataType = {
  [DataTypes.CONTACTS]: getLifecycleStagePropertyOptions,
  [DataTypes.DEALS]: getDealStages
};
const getFunnelDrilldown = (report, keys, options) => {
  const dataType = report.getIn(['config', 'dataType']);
  const pipelineId = report.getIn(['config', 'pipeline', 'id']);
  const reportStages = report.getIn(['config', 'pipeline', 'stages'], List());
  const fetchAllStages = Promise.resolve(getStagesByDataType[dataType](pipelineId));
  const getStages = () => {
    if (reportStages.isEmpty()) {
      return fetchAllStages;
    }
    return fetchAllStages.then(metadataForStage => {
      const stageMetadataMap = metadataForStage.reduce((acc, stageMetadata) => {
        acc[stageMetadata.get('value')] = stageMetadata;
        return acc;
      }, {});
      return reportStages.reduce((acc, stage) => {
        const stageMetdata = stageMetadataMap[stage];
        return stage in stageMetadataMap ? acc.push(stageMetdata) : acc;
      }, List([]));
    });
  };
  return Promise.resolve(getStages()).then(metaDataForStages => {
    const stages = metaDataForStages.map(stage => stage.get('value'));
    const currentStage = keys.first();
    const dateValue = report.getIn(['config', 'filters', 'dateRange', 'value']);
    const useFiscalYear = getUseFiscalYear(report.get('config'));
    const properties = defaultProperties[dataType];
    let filters = report.getIn(['config', 'filters']).set('dateRange', fromJS({
      property: 'createdate',
      value: {
        rangeType: RANGE_TYPES.ALL
      }
    }));
    if (dataType === DEALS) {
      filters = filters.update('custom', List(), custom => custom.push({
        property: 'pipeline',
        operator: 'EQ',
        value: pipelineId
      }));
    }
    if (report.getIn(['config', 'configType']) === PIPELINE) {
      filters = filters.update('custom', List(), custom => custom.push(createFunnelStageFilter(currentStage, dataType, dateValue, useFiscalYear, metaDataForStages)));
    } else {
      const stageIndex = stages.indexOf(currentStage);
      filters = filters.update('custom', List(), custom => custom.concat(stages.take(stageIndex + 1).map(stage => createFunnelStageFilter(stage, dataType, dateValue, useFiscalYear, metaDataForStages))));
    }
    return Config({
      configType: SEARCH,
      limit: 5,
      dataType,
      metrics: properties.map(property => Metric({
        property,
        metricTypes: [COUNT]
      })),
      filters
    });
  }).then(config => buildDrilldownReport(report, config, options));
};
export default {
  isSupported: report => [FUNNEL, PIPELINE].includes(report.getIn(['config', 'configType'])) && [CONTACTS, DEALS].includes(report.getIn(['config', 'dataType'])),
  generate: getFunnelDrilldown,
  trackingName: DRILLDOWN_TYPES.FUNNEL
};